import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { assetUrl, extractYouTubeId } from '../shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO = 'https://images.unsplash.com/photo-1661820030641-35d02e9e9b37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';
const GOLD = '#c8922a';

const eur = (n) => Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export function ProgressBar({ label, total, goal, unit }) {
  const pct = goal > 0 ? Math.min(100, Math.round((total / goal) * 100)) : 0;
  return (
    <div data-testid={`bar-${unit}`} style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{label}</span>
        <span style={{ fontSize: 14, color: '#555' }}>
          <strong style={{ color: GOLD, fontSize: 18 }}>{eur(total)} €</strong> / {eur(goal)} €{unit === 'monthly' ? ' mtl.' : ''}
        </span>
      </div>
      <div style={{ background: '#e9e4da', borderRadius: 999, height: 16, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg,${GOLD},#e0b558)`, borderRadius: 999, transition: 'width .6s ease' }} />
      </div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{pct}% erreicht</div>
    </div>
  );
}

export function PledgeForm({ summary, onDone }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', pledge_type: 'monthly', amount: '', note: '', confirmed: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.confirmed) { setErr('Bitte bestätige das Spendenversprechen.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/hannover/pledge`, { ...form, amount: parseFloat(form.amount) || 0 });
      setDone(true);
      if (onDone) onDone();
    } catch (e) { setErr(e.response?.data?.detail || 'Fehler beim Absenden.'); }
    setBusy(false);
  };

  if (done) return (
    <div data-testid="pledge-success" style={{ textAlign: 'center', padding: '20px 4px' }}>
      <div style={{ fontSize: 44 }}>🙏</div>
      <h3 style={{ color: GOLD, margin: '10px 0' }}>Vielen Dank für deine Zusage!</h3>
      <p style={{ color: '#444' }}>Du erhältst gleich eine Bestätigungs-E-Mail mit unserer Bankverbindung für {form.pledge_type === 'monthly' ? 'deinen Dauerauftrag' : 'deine Überweisung'}.</p>
      {summary && (
        <div style={{ background: '#faf7f2', border: `1px solid ${GOLD}33`, borderRadius: 10, padding: 14, marginTop: 12, textAlign: 'left', fontSize: 14 }}>
          <div><strong>Kontoinhaber:</strong> {summary.account_holder}</div>
          <div><strong>IBAN:</strong> {summary.iban}</div>
          <div><strong>BIC:</strong> {summary.bic}</div>
          <div style={{ marginTop: 6, padding: '6px 8px', background: '#fff6e6', borderRadius: 6, border: `1px solid ${GOLD}` }}><strong>Verwendungszweck (bitte immer angeben):</strong> <span style={{ color: '#8a6412', fontWeight: 800 }}>{summary.purpose}</span></div>
        </div>
      )}
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="pledge-form">
      <p style={{ fontSize: 14, color: '#555', marginTop: 0 }}>Trage dich hier als Unterstützer ein. Du gehst damit ein freiwilliges Spendenversprechen ein.</p>
      <div className="form-group"><label>Name *</label>
        <input className="form-input" data-testid="pledge-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
      <div className="form-group"><label>E-Mail *</label>
        <input className="form-input" type="email" data-testid="pledge-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></div>
      <div className="form-group"><label>Telefon (optional)</label>
        <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
      <div className="form-group"><label>Art der Unterstützung *</label>
        <div style={{ display: 'flex', gap: 10 }}>
          {[['monthly', 'Monatlich (Dauerauftrag)'], ['onetime', 'Einmalig (Umbau)']].map(([v, l]) => (
            <button type="button" key={v} data-testid={`pledge-type-${v}`} onClick={() => setForm({ ...form, pledge_type: v })}
              style={{ flex: 1, padding: '10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                border: form.pledge_type === v ? `2px solid ${GOLD}` : '1px solid #ccc',
                background: form.pledge_type === v ? '#faf3e4' : '#fff', color: form.pledge_type === v ? '#8a6412' : '#555' }}>{l}</button>
          ))}
        </div>
      </div>
      <div className="form-group"><label>Betrag in € *</label>
        <input className="form-input" type="number" min="1" step="1" data-testid="pledge-amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required placeholder={form.pledge_type === 'monthly' ? 'z.B. 25' : 'z.B. 200'} /></div>
      <div className="form-group"><label>Nachricht (optional)</label>
        <textarea className="form-input" rows="2" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#444', margin: '6px 0 14px' }}>
        <input type="checkbox" data-testid="pledge-confirm" checked={form.confirmed} onChange={e => setForm({ ...form, confirmed: e.target.checked })} style={{ marginTop: 3 }} />
        <span>Ich bestätige mein freiwilliges Spendenversprechen und möchte das Projekt Hannover {form.pledge_type === 'monthly' ? 'monatlich' : 'einmalig'} mit dem angegebenen Betrag unterstützen.</span>
      </label>
      {err && <p data-testid="pledge-error" style={{ color: '#c62828', fontSize: 14 }}>{err}</p>}
      <button type="submit" disabled={busy} data-testid="pledge-submit" className="btn-primary" style={{ width: '100%' }}>{busy ? 'Sende…' : 'Jetzt eintragen'}</button>
    </form>
  );
}

export default function HannoverPage() {
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [params] = useSearchParams();

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/hannover/summary`).then(r => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); if (params.get('form') === '1') setShowForm(true); }, [load, params]);

  const s = summary || {};

  return (
    <div style={{ background: '#fff' }}>
      {/* Hero */}
      <div style={{ position: 'relative', minHeight: 420, display: 'flex', alignItems: 'center',
        backgroundImage: `linear-gradient(90deg, rgba(20,18,16,0.92) 0%, rgba(20,18,16,0.55) 60%, rgba(20,18,16,0.2) 100%), url(${HERO})`,
        backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container" style={{ color: '#fff', padding: '60px 20px' }}>
          <div style={{ letterSpacing: 3, color: GOLD, fontWeight: 700, fontSize: 14 }}>HOLYFIRE MINISTRY · HANNOVER</div>
          <h1 style={{ fontSize: 'clamp(34px,6vw,60px)', lineHeight: 1.05, margin: '12px 0', fontWeight: 800 }}>
            Ein Ort, an dem<br /><span style={{ color: GOLD }}>Gemeinde gelebt</span> wird.
          </h1>
          <p style={{ fontSize: 18, maxWidth: 560, color: '#e8e2d8' }}>Jesus im Zentrum. Den Einzelnen im Blick. Begegnung. Zurüstung. Aussendung – für Hannover und darüber hinaus.</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
            <button onClick={() => setShowForm(true)} data-testid="hero-pledge-btn" className="btn-primary" style={{ background: GOLD, border: 'none', padding: '14px 30px', fontSize: 16 }}>Jetzt mittragen</button>
            {s.brochure_file_id && (
              <a href={assetUrl(s.brochure_file_id)} target="_blank" rel="noopener noreferrer" data-testid="hero-brochure-btn" className="btn-secondary" style={{ padding: '14px 26px', fontSize: 16, textDecoration: 'none', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)' }}>📄 Projektmappe öffnen</a>
            )}
          </div>
        </div>
      </div>

      {/* Spendenaufruf-Video (YouTube-Link oder hochgeladene Datei) */}
      {(() => {
        const yt = extractYouTubeId(s.video_url);
        const file = s.video_file_id ? assetUrl(s.video_file_id) : null;
        if (!yt && !file) return null;
        return (
          <section style={{ background: '#141210', padding: '36px 0 40px' }} data-testid="hannover-video-section">
            <div className="container" style={{ maxWidth: 960 }}>
              <div style={{ letterSpacing: 2.5, color: GOLD, fontWeight: 800, fontSize: 13, textAlign: 'center', marginBottom: 14 }}>UNSER SPENDENAUFRUF</div>
              <div style={{ position: 'relative', borderRadius: 18, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.45)', border: `1px solid ${GOLD}55`, background: '#000' }}>
                {yt ? (
                  <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                    <iframe data-testid="hannover-video-youtube" src={`https://www.youtube-nocookie.com/embed/${yt}?rel=0&modestbranding=1`} title="Spendenaufruf Projekt Hannover"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                  </div>
                ) : (
                  <video data-testid="hannover-video-file" src={file} controls playsInline preload="metadata" style={{ display: 'block', width: '100%', maxHeight: 560, background: '#000' }} />
                )}
              </div>
              <p style={{ textAlign: 'center', color: '#cfc7bb', fontSize: 15, margin: '18px auto 0', maxWidth: 640 }}>Andreas &amp; Dorothea erzählen, was in Hannover entsteht – und wie du Teil davon werden kannst.</p>
            </div>
          </section>
        );
      })()}

      {/* Über das Projekt – Was / Wieso / Warum */}
      <section style={{ padding: '52px 0 8px' }} data-testid="hannover-about">
        <div className="container" style={{ maxWidth: 900 }}>
          <div style={{ letterSpacing: 2.5, color: GOLD, fontWeight: 800, fontSize: 13, textAlign: 'center' }}>ÜBER DAS PROJEKT</div>
          <h2 style={{ textAlign: 'center', margin: '10px 0 14px' }}>Worum geht es bei Projekt Hannover?</h2>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: '#444', textAlign: 'center', maxWidth: 720, margin: '0 auto 30px' }}>
            Wir möchten in Hannover rund 216 m² anmieten und daraus einen lebendigen Ort für
            Gemeinde machen – für Begegnung, Zurüstung und Aussendung. Damit dieser Ort entstehen und dauerhaft bestehen kann, tragen wir ihn gemeinsam.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
            {[
              ['Was?', 'Ein fester Standort mit Versammlungs-, Gemeinschafts- und Videostudio-Raum – Heimat für Gottesdienste, Bibelschule, Hauskreise, Worship und Medienarbeit.'],
              ['Wieso?', 'Weil Menschen einen Ort brauchen, an dem sie Jesus begegnen, echte Gemeinschaft erleben und im Glauben wachsen können – mitten in Hannover, offen für alle.'],
              ['Warum jetzt?', 'Die Räume stehen bereit. Mit monatlicher Unterstützung und einmaligen Umbau-Spenden können wir einziehen und den Betrieb dauerhaft sichern.'],
            ].map(([t, d]) => (
              <div key={t} style={{ background: '#faf7f2', border: `1px solid ${GOLD}22`, borderRadius: 14, padding: 22 }}>
                <div style={{ fontWeight: 800, color: GOLD, fontSize: 18, marginBottom: 8 }}>{t}</div>
                <div style={{ fontSize: 14.5, color: '#555', lineHeight: 1.65 }}>{d}</div>
              </div>
            ))}
          </div>

          {s.brochure_file_id && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 28 }}>
              <a href={assetUrl(s.brochure_file_id)} target="_blank" rel="noopener noreferrer" data-testid="about-brochure-btn" className="btn-primary" style={{ background: GOLD, border: 'none', padding: '14px 30px', fontSize: 16, textDecoration: 'none' }}>📄 Projektmappe herunterladen</a>
              <button onClick={() => setShowForm(true)} data-testid="about-pledge-btn" className="btn-secondary" style={{ padding: '14px 26px', fontSize: 16 }}>Jetzt mittragen</button>
            </div>
          )}
        </div>
      </section>

      {/* Fortschritt */}
      <section style={{ background: '#faf7f2', padding: '48px 0' }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <h2 style={{ textAlign: 'center', marginBottom: 6 }}>Gemeinsam tragen wir diesen Ort</h2>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: 30 }}>Anonyme Übersicht der bisherigen Zusagen{s.supporter_count ? ` · ${s.supporter_count} Unterstützer` : ''}</p>
          <ProgressBar label="Monatliche Tragfähigkeit" total={s.monthly_total} goal={s.monthly_goal} unit="monthly" />
          <ProgressBar label="Einmalige Umbau-Spenden" total={s.onetime_total} goal={s.onetime_goal} unit="onetime" />
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button onClick={() => setShowForm(true)} data-testid="progress-pledge-btn" className="btn-primary" style={{ background: GOLD, border: 'none' }}>Ich möchte unterstützen</button>
          </div>
        </div>
      </section>

      {/* Harte Fakten */}
      <section style={{ padding: '48px 0' }}>
        <div className="container" style={{ maxWidth: 900 }}>
          <h2 style={{ textAlign: 'center', marginBottom: 30 }}>Harte Fakten</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
            {[['Standort', 'Hannover'], ['Fläche', 'ca. 216 m²'], ['Miete + NK', 'ca. 2.162 € / Monat'],
              ['Umbau-Budget', '10.000 – 15.000 €'], ['Kaution', '3.893 €'], ['Ziel monatlich', `${eur(s.monthly_goal || 2500)} €`]].map(([k, v]) => (
              <div key={k} style={{ background: '#faf7f2', border: `1px solid ${GOLD}22`, borderRadius: 12, padding: 18, textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a1a', marginTop: 6 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Räume */}
      <section style={{ background: '#141210', color: '#eee', padding: '54px 0' }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <h2 style={{ color: GOLD, textAlign: 'center' }}>Unsere Vision</h2>
          <p style={{ fontSize: 17, lineHeight: 1.8, textAlign: 'center', color: '#d8d2c8' }}>
            {s.intro || 'Wir möchten in Hannover einen Ort schaffen, an dem Menschen Jesus begegnen, Gemeinschaft erleben, zugerüstet werden und das Evangelium weitergetragen wird.'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 18, marginTop: 30 }}>
            {[['Versammlungsraum · 90 m²', 'Seminare, Schulungen, Gottesdienste, Worship und Veranstaltungen.'],
              ['Gemeinschaftsraum · 65 m²', 'Gemeinsames Essen, Hauskreise, Gebet und Gespräche – direkt an Küche & Saal.'],
              ['Videostudio · 23 m²', 'Videozeugnisse, Livestreams und Podcasts – Hoffnung, die Menschen erreicht.'],
              ['Zurüstung', 'Bibelschule, Jüngerschaft, Evangelisationstraining und Seminare unter einem Dach.']].map(([t, d]) => (
              <div key={t} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 20 }}>
                <div style={{ fontWeight: 800, color: GOLD, marginBottom: 6 }}>{t}</div>
                <div style={{ fontSize: 14, color: '#cfc8bd', lineHeight: 1.6 }}>{d}</div>
              </div>
            ))}
          </div>
          {s.floorplan_image_id && (
            <div style={{ marginTop: 34, textAlign: 'center' }}>
              <div style={{ color: GOLD, fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>GRUNDRISS NACH UMBAU</div>
              <img src={assetUrl(s.floorplan_image_id)} alt="Grundriss nach Umbau" data-testid="floorplan-img"
                style={{ maxWidth: '100%', width: 760, borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)', background: '#fff' }} />
            </div>
          )}
        </div>
      </section>

      {/* Kostenübersicht */}
      <section style={{ padding: '54px 0' }}>
        <div className="container" style={{ maxWidth: 900 }}>
          <h2 style={{ textAlign: 'center', marginBottom: 8 }}>Kostenübersicht</h2>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: 26 }}>Transparent aufgeschlüsselt – so setzt sich der Bedarf zusammen.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
            {[
              ['Kaltmiete', '≈ 1.544 € / Monat', 'Gewerberäume ca. 216 m² in Hannover.'],
              ['Nebenkosten', '≈ 618 € / Monat', 'Heizung, Wasser, Strom und weitere laufende Kosten.'],
              ['Monatlicher Bedarf', '≈ 2.500 € / Monat', 'Miete + Nebenkosten + Betrieb – unser monatliches Ziel.'],
              ['Kaution', '3.893 € einmalig', 'Einmalige Mietkaution zum Einzug.'],
              ['Umbau & Ausbau', '10.000 – 15.000 €', 'Trockenbau, Elektrik, Boden, Malerarbeiten für die neuen Räume.'],
              ['Ausstattung', 'nach Bedarf', 'Stühle, Technik, Küche, Studio – auch Sachspenden helfen.'],
            ].map(([k, v, d]) => (
              <div key={k} style={{ border: `1px solid ${GOLD}33`, borderRadius: 12, padding: 20, background: '#fff' }}>
                <div style={{ fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: GOLD, margin: '6px 0' }}>{v}</div>
                <div style={{ fontSize: 14, color: '#555', lineHeight: 1.5 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* So kannst du helfen */}
      <section style={{ padding: '54px 0', background: '#faf7f2' }}>
        <div className="container" style={{ maxWidth: 900 }}>
          <h2 style={{ textAlign: 'center', marginBottom: 26 }}>So kannst du helfen</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 16 }}>
            {[
              ['🙏', 'Beten', 'Trag das Projekt im Gebet mit – dass Gott Räume, Menschen und Mittel zusammenführt.'],
              ['🔁', 'Monatlich geben', 'Sichere mit einem Dauerauftrag die monatliche Tragfähigkeit – schon kleine Beträge zählen.'],
              ['💶', 'Einmalig für den Umbau', 'Unterstütze mit einer einmaligen Spende den Ausbau der Räume.'],
              ['📦', 'Sachspenden & Mithilfe', 'Material, Technik oder Arbeitsleistung beim Umbau – melde dich gern bei uns.'],
            ].map(([ic, t, d]) => (
              <div key={t} style={{ background: '#fff', border: `1px solid ${GOLD}22`, borderRadius: 12, padding: 22, textAlign: 'center' }}>
                <div style={{ fontSize: 34 }}>{ic}</div>
                <div style={{ fontWeight: 800, margin: '8px 0 6px' }}>{t}</div>
                <div style={{ fontSize: 14, color: '#555', lineHeight: 1.55 }}>{d}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button onClick={() => setShowForm(true)} className="btn-primary" style={{ background: GOLD, border: 'none' }} data-testid="help-pledge-btn">Jetzt mittragen</button>
            {s.brochure_file_id && (
              <a href={assetUrl(s.brochure_file_id)} target="_blank" rel="noopener noreferrer" data-testid="help-brochure-btn" className="btn-secondary" style={{ marginLeft: 10, textDecoration: 'none' }}>📄 Projektmappe (PDF)</a>
            )}
          </div>
        </div>
      </section>
      <section style={{ padding: '48px 0', background: '#faf7f2' }}>
        <div className="container" style={{ maxWidth: 620, textAlign: 'center' }}>
          <h2 style={{ marginBottom: 8 }}>Spendenkonto</h2>
          <p style={{ color: '#666' }}>Du kannst auch direkt per Dauerauftrag oder Überweisung unterstützen:</p>
          <div style={{ background: '#fff', border: `1px solid ${GOLD}44`, borderRadius: 12, padding: 22, marginTop: 14, textAlign: 'left', display: 'inline-block' }}>
            <div><strong>Kontoinhaber:</strong> {s.account_holder || 'Holyfire Ministry gUG (haftungsbeschränkt)'}</div>
            <div><strong>IBAN:</strong> {s.iban || 'DE91 2907 0367 0053 6185 00'}</div>
            <div><strong>BIC:</strong> {s.bic || 'DEUTDEHBP21'}</div>
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff6e6', border: `1px solid ${GOLD}`, borderRadius: 8 }}>
              <strong>Verwendungszweck (wichtig!):</strong> <span style={{ color: '#8a6412', fontWeight: 800 }}>{s.purpose || 'Projekt Hannover'}</span>
            </div>
          </div>
          <p style={{ color: '#a15b1e', fontSize: 13, marginTop: 12, fontWeight: 700 }}>⚠️ Bitte bei Dauerauftrag & Überweisung immer „{s.purpose || 'Projekt Hannover'}" als Verwendungszweck angeben – nur so können wir deine Spende dem Projekt korrekt zuordnen.</p>
          <p style={{ color: '#888', fontSize: 13, marginTop: 6 }}>Spenden sind steuerlich absetzbar – Spendenbescheinigung auf Anfrage.</p>
          <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowForm(true)} className="btn-primary" style={{ background: GOLD, border: 'none' }} data-testid="bottom-pledge-btn">Spendenversprechen eintragen</button>
            {s.brochure_file_id && (
              <a href={assetUrl(s.brochure_file_id)} target="_blank" rel="noopener noreferrer" data-testid="bottom-brochure-btn" className="btn-secondary" style={{ textDecoration: 'none' }}>📄 Projektmappe öffnen</a>
            )}
          </div>
        </div>
      </section>

      {/* Formular-Popup */}
      {showForm && (
        <div onClick={() => setShowForm(false)} data-testid="pledge-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 480, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h2 style={{ margin: 0, fontSize: 22 }}>Mittragen</h2>
              <button onClick={() => setShowForm(false)} data-testid="pledge-close" style={{ background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', color: '#999' }}>×</button>
            </div>
            <PledgeForm summary={summary} onDone={load} />
          </div>
        </div>
      )}
    </div>
  );
}
